import React, { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, ShieldCheck } from 'lucide-react';
import { adminGetAccountSignals, adminGetDeviceSettings, adminPatchAccountSignal, adminPatchDeviceSettings } from '../../utils/remote';
import type { AccountSignalRow, AccountSignalStatus, DeviceBindingMode, DeviceSettings } from '../../types/admin';
import {
  Badge, Button, ConfirmDialog, EmptyState, ErrorPanel, Initials, LoadingState, PagedFooter, PageHeader, PanelCard, PillTabs, SortableTh, Switch, Table, Td, Th,
} from '../../school_panel/components/ui';
import { useSortableTable, type SortColumn } from '../../school_panel/lib/useSortableTable';
import { lastSeen } from '../../school_panel/pages/dashboards/sections/format';
import { fieldClass } from '../../school_panel/lib/fieldClass';
import AccountDevicesDrawer from './AccountDevicesDrawer';
import { KIND_KEYS, KIND_LABEL } from './accountSignalKinds';

const PAGE_SIZE = 25;
type StatusKey = AccountSignalStatus | 'all';

const STATUS_LABEL: Record<StatusKey, string> = { open: 'Open', reviewed: 'Reviewed', dismissed: 'Dismissed', all: 'All' };
const SEVERITY_TONE: Record<string, 'warning' | 'neutral'> = { warn: 'warning', info: 'neutral' };
const SEVERITY_LABEL: Record<string, string> = { warn: 'Warning', info: 'Info' };

const MODE_HELP: Record<DeviceBindingMode, string> = {
  off: 'Phone binding is off. Anyone can sign in anywhere (kill switch).',
  grace: 'One phone per student, with the change limit. Old app versions can still sign in until they bind.',
  strict: 'Same as Grace, but old app versions must update to sign in.',
};

const COLUMNS: SortColumn<AccountSignalRow>[] = [
  { key: 'name', accessor: (r) => r.name },
  { key: 'kind', accessor: (r) => r.kind },
  { key: 'severity', accessor: (r) => r.severity },
  { key: 'times_seen', accessor: (r) => r.times_seen, type: 'number' },
  { key: 'first_at', accessor: (r) => r.first_at, type: 'date' },
  { key: 'last_at', accessor: (r) => r.last_at, type: 'date' },
];

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

// --- settings ---------------------------------------------------------------

const PHONE_RE = /^\+?\d{7,15}$/;
const MODES: DeviceBindingMode[] = ['off', 'grace', 'strict'];
const MODE_LABEL: Record<DeviceBindingMode, string> = { off: 'Off', grace: 'Grace', strict: 'Strict' };

type Draft = { mode: DeviceBindingMode; limit: string; window: string; grace: string; code: boolean; schools: boolean; phone: string };

const toDraft = (s: DeviceSettings): Draft => ({
  mode: s.device_binding_mode, limit: String(s.device_switch_limit), window: String(s.device_switch_window_days),
  grace: String(s.device_signout_upload_grace_days), code: s.device_new_device_code, schools: s.device_signals_to_schools,
  phone: s.support_phone ?? '',
});

function intIn(v: string, lo: number, hi: number): number | null {
  if (!/^\d+$/.test(v.trim())) return null;
  const n = Number(v);
  return n >= lo && n <= hi ? n : null;
}

/** Same rules as the server: limit 1-10, window 1-365, grace 1-90, phone empty or 7-15 digits. */
function validateDraft(d: Draft): { errors: Partial<Record<'limit' | 'window' | 'grace' | 'phone', string>>; body: Partial<DeviceSettings> | null } {
  const errors: Partial<Record<'limit' | 'window' | 'grace' | 'phone', string>> = {};
  const limit = intIn(d.limit, 1, 10);
  const win = intIn(d.window, 1, 365);
  const grace = intIn(d.grace, 1, 90);
  if (limit === null) errors.limit = 'Enter a whole number from 1 to 10.';
  if (win === null) errors.window = 'Enter a whole number of days from 1 to 365.';
  if (grace === null) errors.grace = 'Enter a whole number of days from 1 to 90.';
  const phone = d.phone.trim();
  if (phone !== '' && !PHONE_RE.test(phone)) errors.phone = 'Use 7 to 15 digits, with an optional leading +.';
  if (Object.keys(errors).length) return { errors, body: null };
  return {
    errors,
    body: {
      device_binding_mode: d.mode, device_switch_limit: limit as number, device_switch_window_days: win as number,
      device_signout_upload_grace_days: grace as number, device_new_device_code: d.code, device_signals_to_schools: d.schools,
      support_phone: phone,
    },
  };
}

function SettingsCard() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin-device-settings'], queryFn: adminGetDeviceSettings });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [errors, setErrors] = useState<ReturnType<typeof validateDraft>['errors']>({});
  const [msg, setMsg] = useState<{ text: string; error: boolean } | null>(null);
  const [confirmOff, setConfirmOff] = useState<Partial<DeviceSettings> | null>(null);

  useEffect(() => { if (q.data) setDraft(toDraft(q.data)); }, [q.data]);

  const save = useMutation({
    mutationFn: (body: Partial<DeviceSettings>) => adminPatchDeviceSettings(body),
    onSuccess: (res) => { qc.setQueryData(['admin-device-settings'], res); setMsg({ text: 'Settings saved.', error: false }); },
    onError: (err: any) => {
      const data = err?.response?.status === 400 ? err.response.data : null;
      if (!data || typeof data !== 'object') { setMsg({ text: 'The settings were not saved. Check the values and try again.', error: true }); return; }
      const fieldMap: Record<string, 'limit' | 'window' | 'grace' | 'phone'> = {
        device_switch_limit: 'limit', device_switch_window_days: 'window', device_signout_upload_grace_days: 'grace', support_phone: 'phone',
      };
      const fe: ReturnType<typeof validateDraft>['errors'] = {};
      const unknown: string[] = [];
      const other: string[] = [];
      Object.entries(data as Record<string, unknown>).forEach(([k, v]) => {
        if (v === 'unknown') unknown.push(k);
        else if (fieldMap[k]) fe[fieldMap[k]] = 'The server rejected this value.';
        else other.push(k);
      });
      setErrors(fe);
      const parts: string[] = [];
      if (unknown.length) parts.push(`Unknown settings: ${unknown.join(', ')}.`);
      if (other.length) parts.push(`The server rejected: ${other.join(', ')}.`);
      setMsg({ text: parts.length ? `Not saved. ${parts.join(' ')}` : 'Not saved. Fix the highlighted fields.', error: true });
    },
  });

  const set = (patch: Partial<Draft>) => { setDraft((d) => (d ? { ...d, ...patch } : d)); setMsg(null); };
  const submit = () => {
    if (!draft) return;
    const { errors: e, body } = validateDraft(draft);
    setErrors(e);
    if (!body) return;
    // Turning protection off is the kill switch: ask first.
    if (body.device_binding_mode === 'off' && q.data?.device_binding_mode !== 'off') setConfirmOff(body);
    else save.mutate(body);
  };

  const num = (id: string, label: string, key: 'limit' | 'window' | 'grace', hint: string) => (
    <div>
      <label htmlFor={id} className="mb-1 block text-[13px] font-semibold text-ink">{label}</label>
      <input id={id} inputMode="numeric" value={draft?.[key] ?? ''} onChange={(e) => set({ [key]: e.target.value })}
        aria-invalid={errors[key] ? true : undefined} aria-describedby={`${id}-hint`} className={fieldClass} />
      <p id={`${id}-hint`} role={errors[key] ? 'alert' : undefined} className={`mt-1 text-xs ${errors[key] ? 'text-danger-ink' : 'text-muted'}`}>{errors[key] ?? hint}</p>
    </div>
  );

  return (
    <PanelCard className="mb-[18px]">
      <h2 className="mb-1 font-display text-[17px] font-bold tracking-[-0.01em] text-ink">Protection settings</h2>
      <p className="mb-4 text-[13px] text-muted">These apply to every student account on the platform.</p>
      {q.isLoading ? <LoadingState label="Loading settings…" />
        : q.isError || !draft ? <ErrorPanel title="Failed to load" body="The settings could not be loaded." onRetry={() => q.refetch()} />
        : (
          <div>
            <div className="mb-4">
              <PillTabs label="Protection mode" value={draft.mode} onChange={(m) => set({ mode: m })}
                options={MODES.map((m) => ({ key: m, label: MODE_LABEL[m] }))} />
              <p className="mt-1.5 text-xs text-muted">{MODE_HELP[draft.mode]}</p>
            </div>
            <div className="mb-4 grid grid-cols-1 gap-4 min-[700px]:grid-cols-3">
              {num('ds-limit', 'Phone changes allowed', 'limit', '1 to 10')}
              {num('ds-window', 'Within this many days', 'window', '1 to 365 days')}
              {num('ds-grace', 'Upload grace after sign-out (days)', 'grace', '1 to 90 days')}
            </div>
            <div className="mb-4 max-w-[320px]">
              <label htmlFor="ds-phone" className="mb-1 block text-[13px] font-semibold text-ink">Support phone</label>
              <input id="ds-phone" value={draft.phone} onChange={(e) => set({ phone: e.target.value })} placeholder="+251911000000"
                aria-invalid={errors.phone ? true : undefined} aria-describedby="ds-phone-hint" className={fieldClass} />
              <p id="ds-phone-hint" role={errors.phone ? 'alert' : undefined} className={`mt-1 text-xs ${errors.phone ? 'text-danger-ink' : 'text-muted'}`}>
                {errors.phone ?? 'Shown to students who hit the limit. Leave empty for none.'}
              </p>
            </div>
            <div className="mb-4 flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <Switch label="Ask for a code on a new phone" checked={draft.code} onChange={(v) => set({ code: v })} />
                <span className="text-sm text-ink">Ask for a code on a new phone</span>
              </div>
              <div className="flex items-center gap-3">
                <Switch label="Show phone-change counts to school admins" checked={draft.schools} onChange={(v) => set({ schools: v })} />
                <span className="text-sm text-ink">Show phone-change counts to school admins</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="secondary" size="sm" disabled={save.isPending} onClick={submit}>Save settings</Button>
              {msg && <span role={msg.error ? 'alert' : 'status'} className={`text-sm ${msg.error ? 'text-danger-ink' : 'text-ink'}`}>{msg.text}</span>}
            </div>
          </div>
        )}
      <ConfirmDialog isOpen={confirmOff !== null} title="Turn phone binding off?"
        body="Anyone will be able to sign in on any phone, and no phone changes are recorded or limited, until you turn it back on."
        confirmLabel="Turn off" busy={save.isPending}
        onConfirm={() => { if (confirmOff) save.mutate(confirmOff); setConfirmOff(null); }}
        onCancel={() => setConfirmOff(null)} />
    </PanelCard>
  );
}

// --- page -------------------------------------------------------------------

const AccountSharing: React.FC = () => {
  const qc = useQueryClient();
  const [status, setStatus] = useState<StatusKey>('open');
  const [kind, setKind] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [drawerUser, setDrawerUser] = useState<number | null>(null);
  const [rowMsg, setRowMsg] = useState<string | null>(null);
  const debounced = useDebounced(search.trim(), 300);
  const searchPending = search.trim() !== debounced;

  const q = useQuery({
    queryKey: ['admin-account-signals', { status, kind, q: debounced, page }],
    queryFn: () => adminGetAccountSignals({ status, kind: kind || undefined, q: debounced || undefined, page, page_size: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });
  const data = q.data;
  const count = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const columns = useMemo(() => COLUMNS, []);
  const { rows, sort, toggle } = useSortableTable(data?.results, columns, { key: 'last_at', dir: 'desc' });

  const patch = useMutation({
    mutationFn: ({ id, next }: { id: number; next: AccountSignalStatus }) => adminPatchAccountSignal(id, { status: next }),
    onSuccess: () => { setRowMsg(null); qc.invalidateQueries({ queryKey: ['admin-account-signals'] }); },
    onError: (err: any) => setRowMsg(err?.response?.status === 409
      ? 'Another open flag of this kind already exists for that student, so this one cannot be reopened.'
      : 'The flag was not updated. Try again.'),
  });

  const changeStatus = (s: StatusKey) => { setStatus(s); setPage(1); setRowMsg(null); };
  const counts = data?.counts;
  const openCount = counts?.open;

  return (
    <div>
      <PageHeader
        eyebrow="Platform admin · Students"
        title="Account sharing"
        subtitle={openCount !== undefined ? `${openCount} open ${openCount === 1 ? 'flag' : 'flags'} to review. A flag means an account may be used on more phones than one student should need; it is a prompt to look, not proof.` : undefined}
      />

      <SettingsCard />

      <PanelCard>
        <div className="mb-4 max-w-full">
          <PillTabs scroll label="Filter by status" value={status} onChange={changeStatus}
            options={(['open', 'reviewed', 'dismissed', 'all'] as StatusKey[]).map((k) => ({
              key: k, label: STATUS_LABEL[k],
              count: counts ? (k === 'all' ? counts.open + counts.reviewed + counts.dismissed : counts[k]) : undefined,
            }))} />
        </div>
        <div className="mb-4 max-w-full">
          <PillTabs scroll dense label="Filter by kind" value={kind || 'all'} onChange={(k) => { setKind(k === 'all' ? '' : k); setPage(1); setRowMsg(null); }}
            options={[{ key: 'all', label: 'All kinds' }, ...KIND_KEYS.map((k) => ({ key: k, label: KIND_LABEL[k] }))]} />
        </div>
        <div className="mb-4 flex flex-wrap items-center gap-2.5">
          <label className="relative block max-w-[380px] flex-[1_1_260px]">
            <span className="sr-only">Search students</span>
            <Search size={16} aria-hidden className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" />
            <input type="search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Name or username"
              aria-label="Search students" className={`${fieldClass} !min-h-[40px] py-0 pl-10`} />
          </label>
          <span className="ml-auto text-[13px] text-muted">Showing {rows.length} of {count}</span>
        </div>
        {rowMsg && <p role="alert" className="mb-3 text-sm text-danger-ink">{rowMsg}</p>}

        {q.isLoading ? <LoadingState label="Loading flags…" />
          : q.isError ? <ErrorPanel title="Failed to load" body="The flags could not be loaded." onRetry={() => q.refetch()} />
          : rows.length === 0 ? <EmptyState icon={<ShieldCheck size={20} />} title={status === 'open' ? 'No open flags' : 'No flags here'} body="Nothing matches this view." />
          : (
            <Table minWidth={900}>
              <thead>
                <tr>
                  <SortableTh columnKey="name" sort={sort} onToggle={toggle}>Student</SortableTh>
                  <SortableTh columnKey="kind" sort={sort} onToggle={toggle}>Flag</SortableTh>
                  <SortableTh columnKey="severity" sort={sort} onToggle={toggle}>Severity</SortableTh>
                  <SortableTh columnKey="times_seen" sort={sort} onToggle={toggle} align="right">Times seen</SortableTh>
                  <SortableTh columnKey="first_at" sort={sort} onToggle={toggle}>First seen</SortableTh>
                  <SortableTh columnKey="last_at" sort={sort} onToggle={toggle}>Last seen</SortableTh>
                  <Th>Review</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="cursor-pointer" onClick={() => setDrawerUser(r.user_id)}>
                    <Td>
                      <button type="button" onClick={(e) => { e.stopPropagation(); setDrawerUser(r.user_id); }}
                        className="flex items-center gap-3 bg-transparent p-0 text-left" aria-label={`Open devices for ${r.name}`}>
                        <Initials name={r.name} />
                        <span className="min-w-0">
                          <span className="block font-semibold text-ink">{r.name}</span>
                          <span className="block font-mono text-xs font-medium text-muted">@{r.username}</span>
                        </span>
                      </button>
                    </Td>
                    <Td>
                      <span className="block font-medium text-ink">{KIND_LABEL[r.kind] ?? r.kind}</span>
                      <span className="block max-w-[300px] text-xs text-muted">{r.summary}</span>
                    </Td>
                    <Td><Badge tone={SEVERITY_TONE[r.severity] ?? 'neutral'}>{SEVERITY_LABEL[r.severity] ?? r.severity}</Badge></Td>
                    <Td align="right" className="font-mono font-medium text-ink">{r.times_seen}</Td>
                    <Td className="whitespace-nowrap text-muted">{r.first_at ? lastSeen(r.first_at) : '—'}</Td>
                    <Td className="whitespace-nowrap text-muted">{r.last_at ? lastSeen(r.last_at) : '—'}</Td>
                    <Td>
                      <span className="flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                        {r.status === 'open' ? (
                          <>
                            <Button size="xs" variant="ghost" disabled={patch.isPending} onClick={() => patch.mutate({ id: r.id, next: 'reviewed' })}>Mark reviewed</Button>
                            <Button size="xs" variant="ghost" disabled={patch.isPending} onClick={() => patch.mutate({ id: r.id, next: 'dismissed' })}>Dismiss</Button>
                          </>
                        ) : (
                          <Button size="xs" variant="ghost" disabled={patch.isPending} onClick={() => patch.mutate({ id: r.id, next: 'open' })}>Reopen</Button>
                        )}
                      </span>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

        {!q.isLoading && !q.isError && count > 0 && (
          <fieldset disabled={searchPending} className="m-0 min-w-0 border-0 p-0">
            <PagedFooter divider="list" from={(page - 1) * PAGE_SIZE + 1} to={(page - 1) * PAGE_SIZE + rows.length}
              total={count} page={page} pageCount={totalPages} onPage={setPage} label="Flags pagination" />
          </fieldset>
        )}
      </PanelCard>

      <AccountDevicesDrawer userId={drawerUser} onClose={() => setDrawerUser(null)} />
    </div>
  );
};

export default AccountSharing;
